import React, { useState } from 'react';
import { createReturn, ReturnCondition, ReturnType } from '../../services/returnService';
import { ReturnRequest } from '../../types/return';

interface CreateReturnModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const CreateReturnModal: React.FC<CreateReturnModalProps> = ({ onClose, onSuccess }) => {
  const [saleId, setSaleId] = useState<number | ''>('');
  const [itemId, setItemId] = useState<number | ''>('');
  const [qty, setQty] = useState<number>(1);
  const [condition, setCondition] = useState<ReturnCondition>(ReturnCondition.DEFECTIVE);
  const [returnType, setReturnType] = useState<ReturnType>(ReturnType.REFUND);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!saleId || !itemId) {
      alert('Lengkapi semua field');
      return;
    }
    
    setLoading(true);
    try {
      const returnData: ReturnRequest = {
        sale_id: saleId as number,
        items: [{
          item_id: itemId as number,
          qty,
        }],
        condition,
        return_type: returnType,
        reason,
      };
      
      await createReturn(returnData);
      alert('Retur berhasil dibuat!');
      onSuccess();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="p-6 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold">Buat Retur Barang</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {/* Sale ID */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Sale ID / Invoice No</label>
            <input
              type="number"
              value={saleId}
              onChange={(e) => setSaleId(e.target.value ? parseInt(e.target.value) : '')}
              className="w-full px-3 py-2 border rounded"
              placeholder="Masukkan ID transaksi penjualan"
              required
            />
          </div>
          
          {/* Item ID */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Item ID</label>
            <input
              type="number"
              value={itemId}
              onChange={(e) => setItemId(e.target.value ? parseInt(e.target.value) : '')}
              className="w-full px-3 py-2 border rounded"
              placeholder="ID barang yang dikembalikan"
              required
            />
          </div>
          
          {/* Quantity */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Quantity</label>
            <input
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(parseInt(e.target.value))}
              className="w-full px-3 py-2 border rounded"
              required
            />
          </div>
          
          {/* Condition */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Kondisi</label>
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value as ReturnCondition)}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value={ReturnCondition.DEFECTIVE}>Defective/Rusak</option>
              <option value={ReturnCondition.WRONG_ITEM}>Barang Salah</option>
              <option value={ReturnCondition.NOT_AS_DESCRIBED}>Tidak Sesuai Deskripsi</option>
              <option value={ReturnCondition.CHANGED_MIND}>Ganti Pikiran</option>
              <option value={ReturnCondition.OTHER}>Lainnya</option>
            </select>
          </div>
          
          {/* Return Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Jenis Retur</label>
            <select
              value={returnType}
              onChange={(e) => setReturnType(e.target.value as ReturnType)}
              className="w-full px-3 py-2 border rounded"
              required
            >
              <option value={ReturnType.REFUND}>Refund (Uang Kembali)</option>
              <option value={ReturnType.EXCHANGE}>Exchange (Tukar Barang)</option>
            </select>
          </div>
          
          {/* Reason */}
          <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Alasan Retur</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border rounded"
              placeholder="Jelaskan alasan retur..."
              required
            />
          </div>
          
          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 rounded"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Memproses...' : 'Buat Retur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateReturnModal;
